import { useCallback, useEffect, useRef, useState } from "react";
import { motionAPI } from "@blings/blings-player/editor-tools";
import { useMst } from "../../../stores/main";
import { pixelToFrame } from "../utils/timelineHelpers";
import { normalizeAssetId } from "../utils/timingModHelpers";
import { useAnimationJsonEdit } from "./useAnimationJsonEdit";
import {
  clampKeyframeDragDelta,
  getSelectedKeyframeDragBounds,
} from "./dragUtils";

export interface CapturedKeyframeDragItem {
  id: string;
  assetId: string | undefined;
  layerName: string;
  layerUid: number | undefined;
  propertyPath: string;
  keyframeIndex: number;
  initialTime: number;
  compositionTime: number;
}

interface KeyframeDragState {
  isDragging: boolean;
  items: CapturedKeyframeDragItem[];
  allItems: CapturedKeyframeDragItem[];
  anchorId: string | null;
  anchorCompositionTime: number;
  previewDelta: number;
  hasMoved: boolean;
  startClientX: number;
  startClientY: number;
  minDelta: number;
  maxDelta: number;
  retime: KeyframeRetimeState | null;
}

interface KeyframeRetimeState {
  anchorCompositionTime: number;
  draggedCompositionTime: number;
  targetCompositionTime: number;
  direction: "first" | "last";
}

const DRAG_THRESHOLD_PX = 4;

const initialDragState: KeyframeDragState = {
  isDragging: false,
  items: [],
  allItems: [],
  anchorId: null,
  anchorCompositionTime: 0,
  previewDelta: 0,
  hasMoved: false,
  startClientX: 0,
  startClientY: 0,
  minDelta: 0,
  maxDelta: 0,
  retime: null,
};

export interface UseKeyframeDragReturn {
  isDragging: boolean;
  draggedKeyframeId: string | null;
  justDragged: boolean;
  startDrag: (e: React.MouseEvent) => void;
}

function parseOptionalNumber(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseRequiredNumber(value: string | undefined): number | null {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function parseMarkerDragItem(
  element: HTMLElement
): CapturedKeyframeDragItem | null {
  const { dataset } = element;
  const id = dataset.keyframeId;
  const layerName = dataset.layerName;
  const propertyPath = dataset.propertyPath;
  const keyframeIndex = parseRequiredNumber(dataset.keyframeIndex);
  const initialTime = parseRequiredNumber(dataset.resolvedTime);
  const compositionTime = parseRequiredNumber(dataset.compositionTime);

  if (
    !id ||
    !layerName ||
    !propertyPath ||
    keyframeIndex == null ||
    initialTime == null ||
    compositionTime == null
  ) {
    return null;
  }

  return {
    id,
    assetId: normalizeAssetId(dataset.assetId),
    layerName,
    layerUid: parseOptionalNumber(dataset.layerUid),
    propertyPath,
    keyframeIndex,
    initialTime,
    compositionTime,
  };
}

export function getMarkerElements(root: ParentNode): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(".timeline-keyframe-marker")
  );
}

export function getKeyframeDragGroupKey(
  item: CapturedKeyframeDragItem
): string {
  return [
    item.assetId ?? "",
    item.layerUid ?? "",
    item.layerName,
    item.propertyPath,
  ].join("::");
}

export function orderKeyframeDragItemsForApply(
  items: CapturedKeyframeDragItem[],
  delta: number
): CapturedKeyframeDragItem[] {
  return [...items].sort((left, right) => {
    const leftGroupKey = getKeyframeDragGroupKey(left);
    const rightGroupKey = getKeyframeDragGroupKey(right);
    if (leftGroupKey !== rightGroupKey) {
      return leftGroupKey.localeCompare(rightGroupKey);
    }

    if (delta > 0) {
      return right.keyframeIndex - left.keyframeIndex;
    }

    return left.keyframeIndex - right.keyframeIndex;
  });
}

function getSelectedKeyframeRetimeState(
  currentItem: CapturedKeyframeDragItem,
  selectedItems: CapturedKeyframeDragItem[],
  isRetimeRequested: boolean
): KeyframeRetimeState | null {
  if (!isRetimeRequested || selectedItems.length < 2) {
    return null;
  }

  const selectedTimes = selectedItems.map((item) => item.compositionTime);
  const firstCompositionTime = Math.min(...selectedTimes);
  const lastCompositionTime = Math.max(...selectedTimes);
  if (firstCompositionTime === lastCompositionTime) {
    return null;
  }

  if (currentItem.compositionTime === firstCompositionTime) {
    return {
      anchorCompositionTime: lastCompositionTime,
      draggedCompositionTime: firstCompositionTime,
      targetCompositionTime: firstCompositionTime,
      direction: "first",
    };
  }

  if (currentItem.compositionTime === lastCompositionTime) {
    return {
      anchorCompositionTime: firstCompositionTime,
      draggedCompositionTime: lastCompositionTime,
      targetCompositionTime: lastCompositionTime,
      direction: "last",
    };
  }

  return null;
}

function getRetimeStateForDrag(
  dragState: KeyframeDragState,
  isRetimeRequested: boolean
): { retime: KeyframeRetimeState; items: CapturedKeyframeDragItem[] } | null {
  const canReuseRetime = dragState.retime && dragState.items.length > 1;

  const currentItem = dragState.items.find(
    (item) => item.id === dragState.anchorId
  );
  if (!currentItem) {
    return null;
  }

  if (canReuseRetime) {
    return {
      retime: dragState.retime!,
      items: dragState.items,
    };
  }

  const autoPropertyItems = dragState.allItems.filter(
    (item) => getKeyframeDragGroupKey(item) === getKeyframeDragGroupKey(currentItem)
  );
  const retimeItems =
    dragState.items.length > 1 || autoPropertyItems.length < 2
      ? dragState.items
      : autoPropertyItems;
  const retime = getSelectedKeyframeRetimeState(
    currentItem,
    retimeItems,
    isRetimeRequested
  );

  return retime ? { retime, items: retimeItems } : null;
}

function clampRetimeTargetCompositionTime(
  targetCompositionTime: number,
  retime: KeyframeRetimeState
): number {
  if (retime.direction === "first") {
    return Math.min(targetCompositionTime, retime.anchorCompositionTime - 1);
  }

  return Math.max(targetCompositionTime, retime.anchorCompositionTime + 1);
}

function getRetimeScale(retime: KeyframeRetimeState): number {
  return (
    (retime.targetCompositionTime - retime.anchorCompositionTime) /
    (retime.draggedCompositionTime - retime.anchorCompositionTime)
  );
}

function getRetimedCompositionTime(
  item: CapturedKeyframeDragItem,
  retime: KeyframeRetimeState
): number {
  return (
    retime.anchorCompositionTime +
    (item.compositionTime - retime.anchorCompositionTime) *
      getRetimeScale(retime)
  );
}

function getRetimedLocalTime(
  item: CapturedKeyframeDragItem,
  retime: KeyframeRetimeState
): number {
  const timelineOffset = item.compositionTime - item.initialTime;
  return getRetimedCompositionTime(item, retime) - timelineOffset;
}

function getRetimeKeyframeDeltas(
  items: CapturedKeyframeDragItem[],
  retime: KeyframeRetimeState
): Record<string, number> {
  return items.reduce<Record<string, number>>((deltas, item) => {
    deltas[item.id] =
      Math.round(getRetimedLocalTime(item, retime)) - item.initialTime;
    return deltas;
  }, {});
}

function groupKeyframeDragItemsForRetime(items: CapturedKeyframeDragItem[]) {
  const groups = new Map<string, CapturedKeyframeDragItem[]>();
  items.forEach((item) => {
    const groupKey = getKeyframeDragGroupKey(item);
    const group = groups.get(groupKey) ?? [];
    group.push(item);
    groups.set(groupKey, group);
  });
  return Array.from(groups.values());
}

export function useKeyframeDrag(
  compositionStart: number,
  selectedKeyframeIds: Set<string>
): UseKeyframeDragReturn {
  const {
    timelineStore: { zoom, scrollOffset },
    timelineStore,
    undoStore,
  } = useMst();
  const { applyAnimationJsonEdit } = useAnimationJsonEdit();

  const [dragState, setDragState] = useState<KeyframeDragState>(
    initialDragState
  );
  const dragStateRef = useRef(dragState);
  dragStateRef.current = dragState;
  const rafIdRef = useRef<number | null>(null);
  const tracksAreaRef = useRef<HTMLElement | null>(null);
  const justDraggedRef = useRef(false);

  const clearDragState = useCallback(() => {
    setDragState(initialDragState);
    timelineStore.clearKeyframeDragPreview();
    document.body.classList.remove("timeline-keyframe-dragging");
  }, [timelineStore]);

  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const marker = e.currentTarget as HTMLElement;
      const currentItem = parseMarkerDragItem(marker);
      if (!currentItem) {
        return;
      }

      tracksAreaRef.current = marker.closest(
        ".timeline-tracks-area"
      ) as HTMLElement | null;
      const tracksContent = marker.closest(".timeline-tracks__content");
      if (!tracksAreaRef.current || !tracksContent) {
        return;
      }

      const markerItems = getMarkerElements(tracksContent)
        .map((element) => parseMarkerDragItem(element))
        .filter((item): item is CapturedKeyframeDragItem => item !== null);
      const selectedMarkerItems = markerItems.filter((item) =>
        selectedKeyframeIds.has(item.id)
      );

      const selectedItems = selectedMarkerItems.some(
        (item) => item.id === currentItem.id
      )
        ? selectedMarkerItems
        : [currentItem];

      const selectedIds = new Set(selectedItems.map((item) => item.id));
      const { minDelta, maxDelta } = getSelectedKeyframeDragBounds(
        markerItems.map((item) => ({
          id: item.id,
          groupKey: getKeyframeDragGroupKey(item),
          keyframeIndex: item.keyframeIndex,
          initialTime: item.initialTime,
        })),
        selectedIds
      );

      undoStore.onGestureStart();
      timelineStore.setKeyframeDragPreview({
        keyframeIds: selectedItems.map((item) => item.id),
        delta: 0,
        anchorId: currentItem.id,
      });

      setDragState({
        isDragging: true,
        items: selectedItems,
        allItems: markerItems,
        anchorId: currentItem.id,
        anchorCompositionTime: currentItem.compositionTime,
        previewDelta: 0,
        hasMoved: false,
        startClientX: e.clientX,
        startClientY: e.clientY,
        minDelta,
        maxDelta,
        retime: null,
      });
    },
    [selectedKeyframeIds, timelineStore, undoStore]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const currentState = dragStateRef.current;
      if (!currentState.isDragging || !tracksAreaRef.current) return;

      if (rafIdRef.current !== null) return;

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        const nextState = dragStateRef.current;
        if (!nextState.isDragging || !tracksAreaRef.current) return;

        const movedEnough =
          nextState.hasMoved ||
          Math.abs(e.clientX - nextState.startClientX) >= DRAG_THRESHOLD_PX ||
          Math.abs(e.clientY - nextState.startClientY) >= DRAG_THRESHOLD_PX;

        if (!movedEnough) return;

        const rect = tracksAreaRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const absoluteFrame =
          pixelToFrame(mouseX, zoom, scrollOffset) + compositionStart;
        const retimeContext = getRetimeStateForDrag(nextState, e.altKey);
        const retime = retimeContext
          ? {
              ...retimeContext.retime,
              targetCompositionTime: clampRetimeTargetCompositionTime(
                Math.round(absoluteFrame),
                retimeContext.retime
              ),
            }
          : null;
        const previewItems = retimeContext?.items ?? nextState.items;
        const unclampedDelta = Math.round(
          absoluteFrame - nextState.anchorCompositionTime
        );
        const previewDelta = retime
          ? 0
          : clampKeyframeDragDelta(
              unclampedDelta,
              nextState.minDelta,
              nextState.maxDelta
            );
        const keyframeDeltas = retime
          ? getRetimeKeyframeDeltas(previewItems, retime)
          : undefined;

        document.body.classList.add("timeline-keyframe-dragging");

        timelineStore.setKeyframeDragPreview({
          keyframeIds: previewItems.map((item) => item.id),
          delta: previewDelta,
          anchorId: nextState.anchorId ?? previewItems[0]?.id ?? "",
          ...(keyframeDeltas ? { keyframeDeltas } : {}),
        });
        setDragState((previous) => ({
          ...previous,
          items: previewItems,
          previewDelta,
          retime,
          hasMoved: true,
        }));
      });
    },
    [compositionStart, scrollOffset, timelineStore, zoom]
  );

  const handleMouseUp = useCallback(() => {
    const currentState = dragStateRef.current;
    if (!currentState.isDragging) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    if (!currentState.hasMoved) {
      undoStore.onGestureEnd();
      clearDragState();
      return;
    }

    if (currentState.retime) {
      applyAnimationJsonEdit(
        (workingAnimation) => {
          let hasChanges = false;

          groupKeyframeDragItemsForRetime(currentState.items).forEach(
            (items) => {
              const firstItem = items[0];
              const timelineOffset =
                firstItem.compositionTime - firstItem.initialTime;
              const applied = (motionAPI.apply as any).retimePropertyKeyframes(
                workingAnimation as any,
                {
                  assetId: firstItem.assetId,
                  layerName: firstItem.layerName,
                  layerUid: firstItem.layerUid,
                },
                firstItem.propertyPath,
                {
                  selectors: items.map((item) => ({
                    index: item.keyframeIndex,
                  })),
                  anchorTime:
                    currentState.retime!.anchorCompositionTime -
                    timelineOffset,
                  draggedTime:
                    currentState.retime!.draggedCompositionTime -
                    timelineOffset,
                  targetTime:
                    currentState.retime!.targetCompositionTime -
                    timelineOffset,
                  timePrecision: 0,
                }
              );

              if (applied.changes.length) {
                hasChanges = true;
              }
            }
          );

          return hasChanges ? true : false;
        },
        { manageUndo: false }
      );
    } else if (currentState.previewDelta !== 0) {
      applyAnimationJsonEdit(
        (workingAnimation) => {
          let hasChanges = false;

          orderKeyframeDragItemsForApply(
            currentState.items,
            currentState.previewDelta
          ).forEach((item) => {
            const applied = motionAPI.apply.updatePropertyKeyframe(
              workingAnimation as any,
              {
                assetId: item.assetId,
                layerName: item.layerName,
                layerUid: item.layerUid,
              },
              item.propertyPath,
              { index: item.keyframeIndex },
              { time: item.initialTime + currentState.previewDelta }
            );

            if (applied.changes.length) {
              hasChanges = true;
            }
          });

          return hasChanges ? true : false;
        },
        { manageUndo: false }
      );
    }

    undoStore.onGestureEnd();
    justDraggedRef.current = true;
    setTimeout(() => {
      justDraggedRef.current = false;
    }, 0);

    clearDragState();
  }, [applyAnimationJsonEdit, clearDragState, undoStore]);

  useEffect(() => {
    if (!dragState.isDragging) return;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("timeline-keyframe-dragging");
      timelineStore.clearKeyframeDragPreview();
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp, timelineStore]);

  return {
    isDragging: dragState.isDragging,
    draggedKeyframeId: dragState.anchorId,
    justDragged: justDraggedRef.current,
    startDrag,
  };
}
